import React, { PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  CloseIcon,
  FlexColumn,
  FlexRow,
  NavLink,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-design';
import { Modal } from '@block-explorer/ui';

const StyledCloseButton = styled(NavLink)(({ theme }) => ({
  marginRight: '14px',
  path: {
    stroke: theme.styleguideColors.contentSecondary,
  },
  ':hover, :active': {
    path: {
      stroke: theme.styleguideColors.fillPrimaryRed,
    },
  },
}));

const ModalHeader = styled(FlexRow)(({ theme }) => ({
  width: '100%',
  height: '48px',
  position: 'absolute',
  top: 0,
  left: -3,
}));

const ModalFooter = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    // position: 'absolute',
    bottom: 0,
    left: 0,
    // padding: ['20px', '32px', '32px'],
  })
);

const ModalContainer = styled.div(({ theme }) =>
  theme.withMedia({
    height: '100%',
    padding: ['20px', '32px', '32px'],
  })
);

export interface DialogProps {
  isOpen?: boolean;
  onClose?: () => void;
  footer?: React.ReactNode;
}

export const Dialog = ({
  isOpen = false,
  onClose,
  footer,
  children,
}: PropsWithChildren<DialogProps>) => {
  const theme = useTheme();

  const responsiveModalWidth = useMatchMedia(['300px', '512px', '512px'], []);

  const handleCloseOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      onClose && onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          padding: '0',
          position: 'relative',
          borderTop: `5px solid ${theme.styleguideColors.fillPrimaryRed}`,
          borderRadius: theme.borderRadius.base,
          borderWidth: '5px 0 0 0',
          width: responsiveModalWidth,
          minHeight: '200px',
          backgroundColor: theme.styleguideColors.backgroundPrimary,
          border: `1px solid ${theme.styleguideColors.borderPrimary}`,
        },
        overlay: {
          backgroundColor: 'rgba(22, 26, 51, 0.8)',
        },
      }}
    >
      <ModalContainer>
        <ModalHeader align={'center'} justify={'flex-end'}>
          <StyledCloseButton
            onClick={onClose}
            tabIndex={0}
            onKeyDown={handleCloseOnKeyDown}
          >
            <SvgIcon
              src={CloseIcon}
              role={'img'}
              alt={'Cross icon to close modal'}
              size={16}
            />
          </StyledCloseButton>
        </ModalHeader>
        <FlexColumn itemsSpacing={32}>
          {children}
          <ModalFooter>{footer}</ModalFooter>
        </FlexColumn>
      </ModalContainer>
    </Modal>
  );
};
